/**
 * Safe localStorage wrapper with in-memory fallback
 * Handles cases where localStorage is blocked by browser security policies
 */

// In-memory fallback when localStorage is blocked
const memoryStorage: Record<string, string> = {};
let isLocalStorageAvailable: boolean | null = null;

function checkLocalStorageAvailability(): boolean {
  if (isLocalStorageAvailable !== null) return isLocalStorageAvailable;
  try {
    const testKey = "__storage_test__";
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    isLocalStorageAvailable = true;
  } catch {
    isLocalStorageAvailable = false;
    console.warn("localStorage is not available. Using in-memory storage.");
  }
  return isLocalStorageAvailable;
}

export const safeStorage = {
  getItem(key: string): string | null {
    try {
      if (checkLocalStorageAvailability()) {
        return localStorage.getItem(key) ?? memoryStorage[key] ?? null;
      }
      return memoryStorage[key] ?? null;
    } catch {
      return memoryStorage[key] ?? null;
    }
  },

  setItem(key: string, value: string): void {
    try {
      if (checkLocalStorageAvailability()) {
        localStorage.setItem(key, value);
      }
      memoryStorage[key] = value;
    } catch {
      memoryStorage[key] = value;
    }
  },

  removeItem(key: string): void {
    try {
      if (checkLocalStorageAvailability()) {
        localStorage.removeItem(key);
      }
      delete memoryStorage[key];
    } catch {
      delete memoryStorage[key];
    }
  },

  clear(): void {
    try {
      if (checkLocalStorageAvailability()) {
        localStorage.clear();
      }
      Object.keys(memoryStorage).forEach((key) => delete memoryStorage[key]);
    } catch {
      Object.keys(memoryStorage).forEach((key) => delete memoryStorage[key]);
    }
  },

  isAvailable(): boolean {
    return checkLocalStorageAvailability();
  },
};

/**
 * Safely parse JSON with a fallback value
 */
export function safeJsonParse<T>(value: string | null, fallback: T): T {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}
